import { useEffect, useState } from "react";
import DefaultOpenAIKey from "@/components/admin/DefaultOpenAIKey";
import SocialHistory from "@/components/admin/SocialHistory";
import { Send, KeyRound, Phone, Lock, Loader2, Bell, Users2, Plus, Trash2, HelpCircle, MessageSquareText, Lightbulb, Coins } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function SettingsPanel({ activeBusiness, reload, role, onUserChanged }) {
  const isSuper = role === "super_admin";
  const [token, setToken] = useState("");
  const [wa, setWa] = useState("");
  const [savingFonnte, setSavingFonnte] = useState(false);
  const [testing, setTesting] = useState(false);

  const [curPass, setCurPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [savingPass, setSavingPass] = useState(false);
  const [curUsername, setCurUsername] = useState("");
  const [newUsername, setNewUsername] = useState("");

  const [teamNumbers, setTeamNumbers] = useState([]);
  const [tLabel, setTLabel] = useState("");
  const [tNumber, setTNumber] = useState("");
  const [savingTeam, setSavingTeam] = useState(false);

  const [faqs, setFaqs] = useState([]);
  const [fq, setFq] = useState("");
  const [fa, setFa] = useState("");
  const [savingFaq, setSavingFaq] = useState(false);
  const [faqSuggestions, setFaqSuggestions] = useState([]);
  const [draftingIdx, setDraftingIdx] = useState(-1);

  const [aiRate, setAiRate] = useState("5");
  const [aiModel, setAiModel] = useState("gpt-6-luna");
  const [ratePresets, setRatePresets] = useState({});
  const [savingRate, setSavingRate] = useState(false);
  const [activeModel, setActiveModel] = useState("");
  const [modelOptions, setModelOptions] = useState([]);
  const [savingModel, setSavingModel] = useState(false);

  useEffect(() => {
    setToken(activeBusiness?.fonnte_token || "");
    setWa(activeBusiness?.admin_whatsapp || "");
    setFaqs(activeBusiness?.faqs || []);
    if (activeBusiness?.id) {
      api.get("/faq-suggestions", { params: { business_id: activeBusiness.id } })
        .then((r) => setFaqSuggestions(r.data || []))
        .catch(() => setFaqSuggestions([]));
    } else {
      setFaqSuggestions([]);
    }
  }, [activeBusiness]);

  useEffect(() => {
    api.get("/team-numbers").then((r) => setTeamNumbers(r.data || [])).catch(() => {});
    api.get("/auth/me").then((r) => setCurUsername(r.data.user?.username || r.data.username || "admin")).catch(() => {});
    api.get("/ai-rate").then((r) => {
      setAiRate(String(r.data.rupiah_per_ai_call));
      setAiModel(r.data.model || "gpt-6-luna");
      setRatePresets(r.data.presets || {});
    }).catch(() => {});
    api.get("/ai-model").then((r) => {
      setActiveModel(r.data.active);
      setModelOptions(r.data.options || []);
    }).catch(() => {});
  }, []);

  const pickActiveModel = async (model) => {
    setSavingModel(true);
    setActiveModel(model);
    try {
      const r = await api.put("/ai-model", { model });
      setActiveModel(r.data.active);
      setAiRate(String(r.data.rate));
      setAiModel(r.data.active);
      toast.success("Model AI aktif diganti");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingModel(false); }
  };

  const saveAiRate = async (payload) => {
    setSavingRate(true);
    try {
      const r = await api.put("/ai-rate", payload);
      setAiRate(String(r.data.rupiah_per_ai_call));
      setAiModel(r.data.model);
      toast.success("Tarif estimasi AI disimpan");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingRate(false); }
  };

  const pickModel = (model) => {
    setAiModel(model);
    if (model !== "custom") saveAiRate({ model });
  };

  const saveFaqs = async (list) => {
    if (!activeBusiness) { toast.error("Pilih bisnis dulu"); return; }
    setSavingFaq(true);
    try {
      const updated = await api.put(`/businesses/${activeBusiness.id}`, {
        ...activeBusiness,
        faqs: list ?? faqs,
      });
      setFaqs(updated.data.faqs || []);
      toast.success("FAQ disimpan");
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSavingFaq(false); }
  };

  const addFaq = () => {
    if (!fq.trim() || !fa.trim()) { toast.error("Isi pertanyaan & jawaban FAQ"); return; }
    const next = [...faqs, { q: fq.trim(), a: fa.trim() }];
    setFaqs(next);
    setFq(""); setFa("");
    saveFaqs(next);
  };

  const removeFaq = (idx) => {
    const next = faqs.filter((_, i) => i !== idx);
    setFaqs(next);
    saveFaqs(next);
  };

  const applySuggestion = async (question, idx) => {
    setFq(question);
    setFa("");
    setDraftingIdx(idx);
    try {
      const r = await api.post("/faq-suggestions/draft", { question, business_id: activeBusiness?.id });
      setFa(r.data.draft || "");
      toast.success("Draf jawaban dibuat — silakan sunting lalu klik Tambah FAQ");
    } catch (e) {
      toast.error(apiError(e));
      toast.info("Tulis jawaban manual lalu klik Tambah FAQ");
    } finally {
      setDraftingIdx(-1);
      setFaqSuggestions((s) => s.filter((x) => x.question !== question));
      document.querySelector('[data-testid="faq-answer-input"]')?.focus();
    }
  };

  const addTeamNumber = () => {
    const digits = tNumber.replace(/[^0-9]/g, "");
    if (digits.length < 8 || digits.length > 15) { toast.error("Nomor WhatsApp tidak valid (8–15 digit)"); return; }
    const norm = digits.replace(/^0/, "62");
    if (teamNumbers.some((n) => n.number === norm)) { toast.error("Nomor sudah ada"); return; }
    setTeamNumbers([...teamNumbers, { label: tLabel.trim() || norm, number: norm }]);
    setTLabel(""); setTNumber("");
  };

  const saveTeam = async (list) => {
    setSavingTeam(true);
    try {
      const r = await api.put("/team-numbers", { numbers: list ?? teamNumbers });
      setTeamNumbers(r.data);
      toast.success("Daftar nomor tim disimpan");
    } catch (e) { toast.error(apiError(e)); } finally { setSavingTeam(false); }
  };

  const removeTeamNumber = (num) => {
    const next = teamNumbers.filter((n) => n.number !== num);
    setTeamNumbers(next);
    saveTeam(next);
  };

  const saveFonnte = async () => {
    if (!activeBusiness) { toast.error("Pilih bisnis dulu"); return; }
    setSavingFonnte(true);
    try {
      await api.put(`/businesses/${activeBusiness.id}`, {
        ...activeBusiness,
        fonnte_token: token,
        admin_whatsapp: wa,
      });
      toast.success("Pengaturan Fonnte disimpan");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingFonnte(false);
    }
  };

  const testNotif = async () => {
    if (!activeBusiness) return;
    setTesting(true);
    try {
      const r = await api.post(`/businesses/${activeBusiness.id}/test-fonnte`, { fonnte_token: token, admin_whatsapp: wa });
      r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(false);
    }
  };

  const changePass = async () => {
    setSavingPass(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: curPass, new_password: newPass, new_username: newUsername.trim() });
      toast.success("Kredensial berhasil diubah");
      setCurUsername(r.data.username || curUsername);
      if (r.data.user) { localStorage.setItem("currentUser", JSON.stringify(r.data.user)); onUserChanged?.(r.data.user); }
      setCurPass(""); setNewPass(""); setNewUsername("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingPass(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 max-w-4xl">
      {/* Fonnte */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-1">
          <Bell size={18} className="text-[#25D366]" />
          <h2 className="font-heading font-bold">Notifikasi WhatsApp (Fonnte)</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Pengaturan per bisnis: {activeBusiness?.name || "—"}</p>

        {!activeBusiness ? (
          <p className="text-sm text-slate-500">Pilih bisnis dari dropdown di atas.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><KeyRound size={13} /> Token Fonnte</Label>
              <Input value={token} onChange={(e) => setToken(e.target.value)} data-testid="settings-fonnte-token-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1 font-mono-code text-xs" placeholder="Token device Fonnte" />
            </div>
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><Phone size={13} /> Nomor WhatsApp Admin</Label>
              <Input value={wa} onChange={(e) => setWa(e.target.value)} data-testid="settings-admin-whatsapp-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="628xxxxxxxxxx" />
            </div>
            <div className="flex gap-3">
              <Button onClick={saveFonnte} disabled={savingFonnte} data-testid="settings-save-button" className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
                {savingFonnte ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Simpan
              </Button>
              <Button onClick={testNotif} disabled={testing} variant="outline" data-testid="settings-test-notif-button" className="flex-1 border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl">
                {testing ? <Loader2 size={16} className="animate-spin mr-1" /> : <Send size={15} className="mr-1" />} Tes Notif
              </Button>
            </div>
            <p className="text-xs text-slate-600">Dapatkan token di dashboard Fonnte (fonnte.com) &rarr; Device &rarr; Token.</p>
          </div>
        )}
      </div>

      {/* Username & Password */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 h-fit">
        <div className="flex items-center gap-2 mb-1">
          <Lock size={18} className="text-[#2563EB]" />
          <h2 className="font-heading font-bold">Username &amp; Password Admin</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Username aktif: <span className="font-semibold text-slate-700" data-testid="settings-current-username">{curUsername || "admin"}</span></p>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-slate-600">Username Baru (opsional)</Label>
            <Input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} data-testid="settings-new-username" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder={curUsername || "admin"} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Password Saat Ini</Label>
            <Input type="password" value={curPass} onChange={(e) => setCurPass(e.target.value)} data-testid="settings-current-password" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Password Baru (opsional)</Label>
            <Input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="settings-new-password" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" />
          </div>
          <Button onClick={changePass} disabled={savingPass || !curPass || (!newPass && !newUsername.trim())} data-testid="settings-change-password-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
            {savingPass ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Simpan Kredensial
          </Button>
        </div>
      </div>

      {isSuper && (
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 lg:col-span-2" data-testid="team-numbers-card">
        <div className="flex items-center gap-2 mb-1">
          <Users2 size={18} className="text-[#B45309]" />
          <h2 className="font-heading font-bold">Nomor Tim</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">Simpan nomor tim sosmed agar tujuan kirim WhatsApp bisa dipilih cepat tanpa mengetik ulang.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {teamNumbers.length === 0 && <span className="text-sm text-slate-500">Belum ada nomor tim.</span>}
          {teamNumbers.map((n) => (
            <span key={n.number} data-testid={`team-number-${n.number}`} className="flex items-center gap-2 bg-[#F1F5F9] border border-[#E2E8F0] rounded-full pl-3 pr-1 py-1.5 text-sm">
              <span className="font-medium">{n.label}</span>
              <span className="text-xs text-slate-500 font-mono-code">{n.number}</span>
              <button onClick={() => removeTeamNumber(n.number)} data-testid={`team-number-remove-${n.number}`} className="w-5 h-5 rounded-full hover:bg-[#F43F5E]/20 flex items-center justify-center text-slate-600 hover:text-[#E11D48]"><Trash2 size={12} /></button>
            </span>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto_auto] gap-2 items-end">
          <div>
            <Label className="text-xs text-slate-600">Label</Label>
            <Input value={tLabel} onChange={(e) => setTLabel(e.target.value)} data-testid="team-number-label-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Admin / Marketing" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Nomor WhatsApp</Label>
            <Input value={tNumber} onChange={(e) => setTNumber(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTeamNumber()} data-testid="team-number-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="628xxxxxxxxxx" />
          </div>
          <Button type="button" onClick={addTeamNumber} variant="outline" data-testid="team-number-add-button" className="border-[#E2E8F0] bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl"><Plus size={16} /></Button>
          <Button onClick={() => saveTeam()} disabled={savingTeam} data-testid="team-number-save-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
            {savingTeam ? <Loader2 size={16} className="animate-spin" /> : "Simpan"}
          </Button>
        </div>
      </div>
      )}

      {/* FAQ statis per bisnis — jawaban cepat tanpa memanggil AI (hemat token) */}
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 lg:col-span-2" data-testid="faq-card">
        <div className="flex items-center gap-2 mb-1">
          <HelpCircle size={18} className="text-[#059669]" />
          <h2 className="font-heading font-bold">FAQ Statis Chatbot</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Pertanyaan umum dijawab langsung dari daftar ini <span className="text-slate-600">tanpa memanggil AI</span> — hemat token. Berlaku untuk bisnis: {activeBusiness?.name || "—"}
        </p>

        {!activeBusiness ? (
          <p className="text-sm text-slate-500">Pilih bisnis dari dropdown di atas.</p>
        ) : (
          <>
            <div className="space-y-2 mb-5">
              {faqs.length === 0 && <span className="text-sm text-slate-500">Belum ada FAQ. Tambahkan di bawah.</span>}
              {faqs.map((f, idx) => (
                <div key={idx} data-testid={`faq-item-${idx}`} className="flex items-start gap-3 bg-[#F1F5F9] border border-[#E2E8F0] rounded-xl p-3">
                  <MessageSquareText size={15} className="text-[#059669] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800">{f.q}</p>
                    <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{f.a}</p>
                  </div>
                  <button onClick={() => removeFaq(idx)} data-testid={`faq-remove-${idx}`} className="w-6 h-6 rounded-lg hover:bg-[#F43F5E]/20 flex items-center justify-center text-slate-600 hover:text-[#E11D48] shrink-0"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
            {faqSuggestions.length > 0 && (
              <div className="mb-5 bg-[#F1F5F9] border border-[#F59E0B]/30 rounded-xl p-4" data-testid="faq-suggestions">
                <p className="text-xs text-[#B45309] mb-3 flex items-center gap-1.5">
                  <Lightbulb size={14} /> Saran FAQ dari pertanyaan pelanggan yang sering muncul:
                </p>
                <div className="space-y-2">
                  {faqSuggestions.map((s, i) => (
                    <div key={i} data-testid={`faq-suggestion-${i}`} className="flex items-center justify-between gap-3 bg-[#FFFFFF] border border-[#E2E8F0] rounded-lg px-3 py-2">
                      <span className="text-sm text-slate-700 truncate">{s.question} <span className="text-[11px] text-slate-500">({s.count}x)</span></span>
                      <button onClick={() => applySuggestion(s.question, i)} disabled={draftingIdx !== -1} data-testid={`faq-suggestion-use-${i}`} className="text-xs text-[#2563EB] hover:underline flex items-center gap-1 shrink-0 disabled:opacity-50">
                        {draftingIdx === i ? <><Loader2 size={12} className="animate-spin" /> Menyusun...</> : <><Plus size={12} /> Jadikan FAQ</>}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <div>
                <Label className="text-xs text-slate-600">Pertanyaan</Label>
                <Input value={fq} onChange={(e) => setFq(e.target.value)} data-testid="faq-question-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Mis. Apakah ada pengiriman ke luar kota?" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Jawaban</Label>
                <Textarea value={fa} onChange={(e) => setFa(e.target.value)} rows={2} data-testid="faq-answer-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Mis. Ya, kami kirim ke seluruh Indonesia via JNE/J&T." />
              </div>
              <Button onClick={addFaq} disabled={savingFaq} data-testid="faq-add-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
                {savingFaq ? <Loader2 size={16} className="animate-spin mr-1" /> : <Plus size={16} className="mr-1" />} Tambah FAQ
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Pilihan provider/model AI yang benar-benar dipakai aplikasi */}
      {isSuper && (
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 lg:col-span-2" data-testid="ai-model-card">
        <div className="flex items-center gap-2 mb-1">
          <Coins size={18} className="text-[#3B82F6]" />
          <h2 className="font-heading font-bold">Model AI Aktif</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Model yang dipilih dipakai chatbot, generator konten (termasuk artikel panjang), ide konten, dan draf FAQ untuk semua bisnis.
        </p>
        <div className="max-w-sm">
          <Label className="text-xs text-slate-600">Penyedia &amp; Model</Label>
          <Select value={activeModel} onValueChange={pickActiveModel} disabled={savingModel}>
            <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="ai-model-select"><SelectValue placeholder="Pilih model" /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
              {modelOptions.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`ai-model-${o.value}`}>{o.label} — ~Rp{o.rate}/panggilan</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-2" data-testid="ai-model-active">
            {savingModel ? "Menyimpan..." : `Aktif: ${activeModel}`}
          </p>
          <p className="text-[11px] text-slate-500 mt-2">Angka Rupiah adalah estimasi aplikasi. Biaya API aktual mengikuti penggunaan token dan tarif provider.</p>
        </div>
      </div>
      )}

      {/* Kalibrasi tarif estimasi penghematan AI */}
      {isSuper && (
      <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl p-4 sm:p-6 lg:col-span-2" data-testid="ai-rate-card">
        <div className="flex items-center gap-2 mb-1">
          <Coins size={18} className="text-[#A855F7]" />
          <h2 className="font-heading font-bold">Tarif Estimasi AI</h2>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          Perkiraan biaya 1 panggilan AI chatbot (dalam Rupiah). Dipakai untuk menghitung estimasi penghematan di Dashboard — pilih model AI Anda, atau gunakan tarif kustom.
        </p>
        <div className="space-y-3 max-w-sm">
          <div>
            <Label className="text-xs text-slate-600">Model AI chatbot</Label>
            <Select value={aiModel} onValueChange={pickModel}>
              <SelectTrigger className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" data-testid="ai-rate-model-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                {Object.entries(ratePresets).map(([m, rp]) => (
                  <SelectItem key={m} value={m} data-testid={`ai-rate-model-${m}`}>{m} — ~Rp{rp}/panggilan</SelectItem>
                ))}
                <SelectItem value="custom" data-testid="ai-rate-model-custom">Kustom (atur sendiri)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {aiModel === "custom" && (
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs text-slate-600">Rp per panggilan AI</Label>
                <Input type="number" min="0" value={aiRate} onChange={(e) => setAiRate(e.target.value)} data-testid="ai-rate-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="5" />
              </div>
              <Button onClick={() => saveAiRate({ rupiah_per_ai_call: parseInt(aiRate, 10) || 0 })} disabled={savingRate} data-testid="ai-rate-save-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
                {savingRate ? <Loader2 size={16} className="animate-spin" /> : "Simpan"}
              </Button>
            </div>
          )}
          <p className="text-xs text-slate-600" data-testid="ai-rate-current">
            Tarif aktif: <span className="font-semibold text-[#A855F7]">Rp{Number(aiRate || 0).toLocaleString("id-ID")}</span> per panggilan AI{aiModel !== "custom" ? ` (${aiModel})` : ""}
          </p>
        </div>
      </div>
      )}
      {isSuper && <DefaultOpenAIKey />}
      {role !== "operator" && <SocialHistory businessId={activeBusiness?.id} />}
    </div>
  );
}
